/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

function formatStickyPack(value) {
  if (!value) return '';
  const normalized = value
    .replace(/\s*pack\s*/gi, ' ')
    .trim()
    .toLowerCase();
  return normalized ? `${normalized} pack` : '';
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.variantPicker = this.section.querySelector('variant-picker');
        this.designEl = this.querySelector('[data-sticky-design]');
        this.packEl = this.querySelector('[data-sticky-pack]');
        this.changeLink = this.querySelector('.sticky-atc__change');

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);
        window.addEventListener('resize', this.updateKlaviyoOffset.bind(this));

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

        if (this.changeLink) {
          this.changeLink.addEventListener('click', this.onChangeClick.bind(this));
        }

        this.klaviyoObserver = new MutationObserver(() => {
          this.updateKlaviyoOffset();
        });
        this.klaviyoObserver.observe(document.body, { childList: true, subtree: true, attributes: true });

        this.classList.remove('invisible');
        this.updateKlaviyoOffset();
        StickyAtc.handleScroll.call(this);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.updateKlaviyoOffset);
        this.klaviyoObserver?.disconnect();
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('.product-column-right .product-form');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky')
          ? document.querySelector('.section-header').offsetHeight
          : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );

        this.updateKlaviyoOffset();
      }

      /**
       * Shift right when a Klaviyo teaser is visible in the bottom-left corner.
       */
      updateKlaviyoOffset() {
        const klTeaser = document.querySelector('[class*="kl-teaser"]');
        const isLeftPosition = this.classList.contains('sticky-atc--start');
        let offset = 0;

        if (klTeaser && isLeftPosition) {
          const rect = klTeaser.getBoundingClientRect();
          const isVisible = rect.width > 0
            && rect.height > 0
            && rect.left < window.innerWidth * 0.5
            && rect.bottom > window.innerHeight - 24;

          if (isVisible) {
            offset = Math.ceil(rect.right) + 16 - (parseFloat(
              getComputedStyle(document.documentElement).getPropertyValue('--container-pad-x')
            ) || 16);
            offset = Math.max(offset, 0);
          }
        }

        this.classList.toggle('sticky-atc--klaviyo-offset', offset > 0);
        this.style.setProperty('--sticky-atc-klaviyo-offset', `${offset}px`);
      }

      /**
       * Scroll to the product options anchor when Change is clicked.
       * @param {Event} evt - Click event.
       */
      onChangeClick(evt) {
        const target = this.section?.querySelector('.product-column-right .product-form');
        if (!target || typeof theme.scrollToRevealElement !== 'function') return;

        evt.preventDefault();
        theme.scrollToRevealElement(target);
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.updateVariantLabels(evt.detail.selectedOptions);
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.updateVariantLabels(null);
          if (idInput) idInput.value = '';
        }

        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Updates design and pack labels from selected options.
       * @param {?Array<string>} selectedOptions - Selected option values.
       */
      updateVariantLabels(selectedOptions) {
        if (!selectedOptions) {
          if (this.designEl) {
            this.designEl.textContent = '';
            this.designEl.hidden = true;
          }
          if (this.packEl) {
            this.packEl.textContent = '';
            this.packEl.hidden = true;
          }
          return;
        }

        let design = '';
        let pack = '';

        selectedOptions.forEach((value, index) => {
          if (!value) return;

          const selector = this.variantPicker?.querySelector(`[data-option-index="${index}"]`);
          const optionName = (
            selector?.querySelector('[data-variant-name]')?.dataset.variantName || ''
          ).toLowerCase();

          if (optionName.includes('pack')) {
            pack = formatStickyPack(value);
          } else {
            design = value;
          }
        });

        if (this.designEl) {
          this.designEl.textContent = design;
          this.designEl.hidden = !design;
        }

        if (this.packEl) {
          this.packEl.textContent = pack;
          this.packEl.hidden = !pack;
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock
          : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
